import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useLocaleContext } from '@/website/contexts/AppContext';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { getLegalLinkHrefWithCountry, getLegalLinks } from '@/website/util';
import { socialLinks } from '@/website/util';
import {
  AmericanCountryList,
  EuropeanUnionList,
  PromotionEntityType,
  REQUEST_SUCCESS_CODE,
  SimpleRevisionTypeEnums,
  allCountryInfoMapList,
  apiGetDocumentDetailInfoWithCommonInterface,
  openUrlWithNewWindow,
  useLocalTranslation,
} from '@jove-frontend/uikit';
import { Box, Typography } from '@mui/material';

const BrokerLandingPageFooter = () => {
  const router = useRouter();
  const { $t } = useLocalTranslation();
  const [companyRegistrationText, setCompanyRegistrationText] = useState('');
  const [addressText, setAddressText] = useState('');
  const {
    state: { promotionInfo },
  } = promotionInfoStore();
  const [socialLinkList, setSocialLinkList] = useState([]);
  const [brokerLegalLinkList, setBrokerLegalLinkList] = useState([]);
  const [joveLegalLinkList] = useState(
    getLegalLinks().filter((item) => ['privacy-policy', 'cookie-policy', 'terms-and-conditions', 'personal-data-retention-policy'].includes(item.key))
  );
  const [brokerEntityInfo, setBrokerEntityInfo] = useState({
    name: '',
    frn: '',
    fca: '',
  });
  const { country } = useLocaleContext();
  const [partnerFooterInfo, setPartnerFooterInfo] = useState({
    name: '',
    countryText: '',
    companyNumber: '',
  });
  const [footerIsCustom, setFooterIsCustom] = useState(false);

  /**
   * request
   */
  const requestViewDocument = async (item) => {
    const [res] = await apiGetDocumentDetailInfoWithCommonInterface({
      id: item?.id,
    });
    if (res?.code === REQUEST_SUCCESS_CODE) {
      openUrlWithNewWindow(res.data);
    }
  };

  /**
   * effects
   */
  useEffect(() => {
    evaluateFooterDisplayInfo();
    if ([PromotionEntityType.PARTNER_PROMOTION].includes(promotionInfo?.promotion_type)) {
      evaluatePartnerFooterInfo();
    } else {
      evaluateBrokerEntityInfo();
    }
  }, [promotionInfo]);

  useEffect(() => {
    evaluateCompanyInfoText();
  }, [country]);

  /**
   * methods
   */
  const evaluateCompanyInfoText = async () => {
    let registText = '';
    let addressText = '';
    if (EuropeanUnionList.includes(country)) {
      registText = $t('page:footer.jove_eu_registration');
      addressText = $t('page:footer.jove_eu_address');
    } else if (AmericanCountryList.includes(country)) {
      registText = $t('page:footer.jove_us_registration');
      addressText = $t('page:footer.jove_us_address');
    } else {
      registText = $t('page:footer.jove_uk_registration');
      addressText = $t('page:footer.jove_uk_address');
    }
    setCompanyRegistrationText(registText);
    setAddressText(addressText);
  };
  const evaluateFooterDisplayInfo = () => {
    const { legal_term, social_media, footer } = promotionInfo?.payload?.website_configuration || {};
    const { quote_terms_document, privacy_policy_document } = promotionInfo?.payload || {};
    const isCustom = footer === 'custom';
    const promotionHaveOwnFile = (data) => {
      return (
        (data?.revision_type === SimpleRevisionTypeEnums.FILE_URL && !!data?.file_url) ||
        (data?.revision_type !== SimpleRevisionTypeEnums.FILE_URL && !!data?.id)
      );
    };

    let list = [];
    if (promotionHaveOwnFile(privacy_policy_document)) {
      list.push({
        ...privacy_policy_document,
        href: privacy_policy_document?.file_url || '',
        name: $t('page:brokerLandingPageFooter.privacy_policy'),
        type: privacy_policy_document?.revision_type === SimpleRevisionTypeEnums.FILE_URL ? 'link' : 'file',
      });
    }
    if (promotionHaveOwnFile(quote_terms_document)) {
      list.push({
        ...quote_terms_document,
        href: quote_terms_document?.file_url || '',
        name: $t('page:brokerLandingPageFooter.terms_and_conditions'),
        type: quote_terms_document?.revision_type === SimpleRevisionTypeEnums.FILE_URL ? 'link' : 'file',
      });
    }
    list.push(
      ...(legal_term || []).map((item) => {
        return {
          href: item.url,
          name: item.name,
          type: 'link',
        };
      })
    );
    setBrokerLegalLinkList(list);
    setSocialLinkList(isCustom ? social_media || [] : socialLinks);
    setFooterIsCustom(isCustom);
  };

  const evaluateBrokerEntityInfo = () => {
    let item = promotionInfo?.brokerEntityList?.find((item) => item.id === router.query['entity_id']);
    if (!item) {
      item = promotionInfo?.brokerEntityList?.[0];
    }

    if (item) {
      const { name, regulated_body_name, insurance_license_number } = item;
      setBrokerEntityInfo({
        name,
        fca: regulated_body_name || 'Financial Conduct Authority',
        frn: insurance_license_number,
      });
    }
  };

  const evaluatePartnerFooterInfo = () => {
    const countryList = [];
    (promotionInfo?.payload?.website_configuration?.footer_information?.entity_countries || []).forEach((code) => {
      const item = allCountryInfoMapList.find((item) => item.value === code);
      if (item) {
        countryList.push(item.label);
      }
    });
    setPartnerFooterInfo({
      name: promotionInfo?.brokerName,
      countryText: countryList.join(', '),
      companyNumber: promotionInfo?.payload?.website_configuration?.additional_information?.registerd_number,
    });
  };

  const openSocialLinkAction = (item) => {
    openUrlWithNewWindow(item.url);
  };

  const clickJoveLegalLinkAction = (e, item) => {
    e?.preventDefault();
    openUrlWithNewWindow(window.location.origin + getLegalLinkHrefWithCountry(item.href, country));
  };

  const clickCustomLegalLinkAction = (e, item) => {
    e?.preventDefault();
    if (item.type === 'file') {
      requestViewDocument(item);
    } else {
      openUrlWithNewWindow(item.href);
    }
  };

  return (
    <Box className="flex flex-row justify-center bg-black px-10 py-6">
      <Box className="flex flex-row space-x-10">
        <Box className="flex flex-1 flex-col items-center">
          <Box className="flex flex-col space-y-10">
            <Box className="flex flex-col items-center space-y-3">
              {footerIsCustom && brokerLegalLinkList?.length > 0 && (
                <Box className="self-start">
                  {brokerLegalLinkList.map((item, index) => (
                    <Link
                      href={item.href}
                      passHref
                      key={item.name + index}
                      className="ml-4 border-r border-r-white pr-4 text-sm leading-3 text-white first:ml-0 last:border-r-0 hover:underline"
                      onClick={(e) => clickCustomLegalLinkAction(e, item)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </Box>
              )}
              {[PromotionEntityType.PARTNER_PROMOTION].includes(promotionInfo?.promotion_type) && (
                <>
                  <Typography variant="subtitle2" className="text-gray-400">
                    {$t('page:brokerLandingPageFooter.partner_footer_part1')}
                    <span className="font-black">{$t('page:brokerLandingPageFooter.partner_footer_jove_technology')}</span>
                    {$t('page:brokerLandingPageFooter.partner_footer_part2')}
                    <span className="font-black">{partnerFooterInfo?.name}</span>
                    {$t('page:brokerLandingPageFooter.partner_footer_part3')}
                  </Typography>
                  <Typography variant="subtitle2" className="text-gray-400">
                    {$t('page:brokerLandingPageFooter.registered_address')}:{' '}
                    {promotionInfo?.payload?.website_configuration?.additional_information?.registered_address}.
                  </Typography>
                  <Typography variant="subtitle2" className="leading-4 text-gray-400">
                    &copy;{' '}
                    {$t('page:brokerLandingPageFooter.partner_footer_copyright', {
                      year: new Date().getFullYear(),
                      name: partnerFooterInfo?.name,
                      country: partnerFooterInfo?.countryText,
                      companyNumber: partnerFooterInfo?.companyNumber,
                    })}
                  </Typography>
                </>
              )}
              {[PromotionEntityType.BROKER_PROMOTION].includes(promotionInfo?.promotion_type) && (
                <>
                  {/* TODO: Quick fix to display the Special Insurance MDS footer as requested for pt-PT.
                       Needs to be properly standardized (e.g. driven by promotion config) in a future iteration. */}
                  {brokerEntityInfo?.name === 'Special Insurance' ? (
                    <Typography variant="subtitle2" className="leading-4 text-gray-400">
                      {$t('page:brokerLandingPageFooter.broker_mds_footer_part_1')}
                      <a href="https://www.asf.com.pt" target="_blank" rel="noreferrer" className="underline">
                        www.asf.com.pt
                      </a>
                      {'.'}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="subtitle2" className="leading-4 text-gray-400">
                        <span className="font-black">{brokerEntityInfo?.name}</span>
                        {$t('page:brokerLandingPageFooter.broker_footer_part1')}
                        {brokerEntityInfo?.fca}
                        {$t('page:brokerLandingPageFooter.broker_footer_part2')}
                        {brokerEntityInfo?.frn}
                        {$t('page:brokerLandingPageFooter.broker_footer_part3')}
                        <span className="font-black">{brokerEntityInfo?.name}</span>
                        {$t('page:brokerLandingPageFooter.broker_footer_part4')}
                        <span className="font-black">{brokerEntityInfo?.name}</span>
                        {$t('page:brokerLandingPageFooter.broker_footer_part5')}
                      </Typography>
                      <Typography variant="subtitle2" className="text-gray-400">
                        {$t('page:brokerLandingPageFooter.registered_address')}:{' '}
                        {promotionInfo?.payload?.website_configuration?.additional_information?.registered_address}.{' '}
                        {$t('page:brokerLandingPageFooter.broker_footer_company_info', {
                          companyNumber: promotionInfo?.payload?.website_configuration?.additional_information?.registerd_number,
                        })}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Box>
            <Box className="flex flex-col items-center space-y-2">
              <Box className="self-start">
                {joveLegalLinkList.map((item, index) => (
                  <Link
                    href={item.href}
                    passHref
                    key={item.name}
                    className="ml-4 border-r border-r-white pr-4 text-sm leading-3 text-white first:ml-0 last:border-r-0 hover:underline"
                    onClick={(e) => clickJoveLegalLinkAction(e, item)}
                  >
                    {item.name}
                  </Link>
                ))}
              </Box>
              {brokerEntityInfo?.name === 'Special Insurance' ? (
                <>
                  {/* <Typography variant="subtitle2" className="leading-4 text-gray-400">
                    {$t('page:brokerLandingPageFooter.broker_mds_footer_part_2_1')}
                    <a href="https://www.asf.com.pt" target="_blank" rel="noreferrer" className="underline">
                      www.asf.com.pt
                    </a>
                    {'.'}
                  </Typography> */}
                  <Typography variant="subtitle2" className="leading-4 text-gray-400">
                    {$t('page:brokerLandingPageFooter.broker_mds_footer_s_in_contract_part1')}
                    {$t('page:brokerLandingPageFooter.broker_mds_footer_s_in_contract_part2')}
                    <span className="font-black">{$t('page:brokerLandingPageFooter.broker_mds_footer_s_in_contract_part3')}</span>
                  </Typography>
                  <Typography variant="subtitle2" className="leading-4 text-gray-400">
                    {$t('page:brokerLandingPageFooter.broker_mds_footer_part_2_2')}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" className="leading-4 text-gray-400">
                    {$t('page:brokerLandingPageFooter.online_services_part1')}
                    <span className="font-black">{$t('page:brokerLandingPageFooter.online_services_jove_technology')}</span>
                    {$t('page:brokerLandingPageFooter.online_services_part2')}
                    <span className="font-black">{$t('page:brokerLandingPageFooter.online_services_jove_does_not_advise')}</span>
                    {$t('page:brokerLandingPageFooter.online_services_part3')}
                    <span className="font-black">{brokerEntityInfo?.name}</span>
                    {$t('page:brokerLandingPageFooter.online_services_part4')}
                    <span className="font-black">{$t('page:brokerLandingPageFooter.online_services_jove_authorized')}</span>
                    {$t('page:brokerLandingPageFooter.online_services_part5')}
                    <span className="font-black">{$t('page:brokerLandingPageFooter.online_services_jove_authorized')}</span>
                    {$t('page:brokerLandingPageFooter.online_services_part6')}
                  </Typography>
                </>
              )}
              <Typography variant="subtitle2" className="leading-4 text-gray-400">
                {$t('page:brokerLandingPageFooter.registered_in_england_wales')} (No. 12846957).
              </Typography>
              <Typography variant="subtitle2" className="leading-4 text-gray-400">
                {$t('page:brokerLandingPageFooter.copyright_jove_limited', { year: new Date().getFullYear() })}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className="mt-5 flex flex-row space-x-5">
          {socialLinkList.map((item, index) => {
            return (
              <img key={index} src={item.logo_url} alt={item.name} className="h-6 w-auto cursor-pointer" onClick={() => openSocialLinkAction(item)} />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default BrokerLandingPageFooter;
